import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Divider,
  CircularProgress, IconButton, Tooltip
} from '@mui/material';
import {
  AccountTree, Receipt, Payment, AccountBalance, Business,
  Description, HomeWork, Inventory2, ShoppingCart, ListAlt,
  SwapHoriz, EditNote, LocationOn, MonetizationOn, People,
  ReceiptLong, Numbers, Tune, PendingActions, Warning,
  CheckCircleOutline, TrendingUp, Refresh, Home, Category, Settings,
  PieChart, Balance, Assessment, HourglassBottom, Payments, AccountBalanceWallet, Lock,
  RequestQuote, ArrowUpward, ArrowDownward, ManageSearch
} from '@mui/icons-material';
import axios from 'axios';
import { fmt, fmtN, sym } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;

const FinanceDashboard = () => {
  const navigate = useNavigate();
  const [kpis,    setKpis]    = useState(null);
  const [cash,    setCash]    = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchKpis = useCallback(async () => {
    setLoading(true);
    const yearStart = `${new Date().getFullYear()}-01-01`;
    const [invR, recR, pinvR, pvR, bankR, cashR] = await Promise.allSettled([
      axios.get(`${API}/api/finance/invoices`,      { headers }),
      axios.get(`${API}/api/finance/payments`,       { headers }),
      axios.get(`${API}/api/payables/invoices`,      { headers }),
      axios.get(`${API}/api/payables/vouchers`,      { headers }),
      axios.get(`${API}/api/finance/banks`,          { headers }),
      axios.get(`${API}/api/finance/reports/cash-summary?from=${yearStart}`, { headers }),
    ]);

    const val  = (r) => r.status === 'fulfilled' ? r.value.data : [];
    const invs = invR.status === 'fulfilled' ? (invR.value.data?.data || []) : [];
    const recs = recR.status === 'fulfilled' ? (recR.value.data?.data || []) : [];
    const now  = new Date();
    const mStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const openInv     = invs.filter(i => ['Open', 'Partial'].includes(i.status));
    const overdueInv  = openInv.filter(i => i.dueDate && new Date(i.dueDate) < now);
    const openInvAmt  = openInv.reduce((s, i) => s + ((i.amount || 0) - (i.paid || 0)), 0);
    const monthRecs   = recs.filter(r => new Date(r.paymentDate || r.createdAt) >= mStart);
    const monthAmt    = monthRecs.reduce((s, r) => s + (r.amount || 0), 0);

    const pinvs       = val(pinvR);
    const pvs         = val(pvR);
    const openPinv    = pinvs.filter(i => i.status !== 'Paid' && i.status !== 'Cancelled');
    const pendingPv   = pvs.filter(v => v.status === 'Open' || v.status === 'Draft');

    setKpis({
      openInvCount:   openInv.length,
      openInvAmt,
      overdueCount:   overdueInv.length,
      monthRecCount:  monthRecs.length,
      monthAmt,
      openPinvCount:  openPinv.length,
      pendingPvCount: pendingPv.length,
      bankCount:      val(bankR).length,
    });
    setCash(cashR.status === 'fulfilled' ? cashR.value.data : null);
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  // ── KPI Cue ────────────────────────────────────────────────────────────────
  const Cue = ({ icon: Icon, label, value, sub, color, urgent, path }) => (
    <Card elevation={2} sx={{
      borderRadius: 3, cursor: path ? 'pointer' : 'default', height: '100%',
      borderLeft: `4px solid ${urgent ? '#c62828' : color}`,
      transition: 'transform 0.15s, box-shadow 0.15s',
      '&:hover': path ? { boxShadow: 5, transform: 'translateY(-2px)' } : {}
    }} onClick={() => path && navigate(path)}>
      <CardContent sx={{ py: 2 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h4" fontWeight={800} color={urgent ? 'error.main' : color} lineHeight={1}>
              {value}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary" mt={0.5}>{label}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${urgent ? '#c62828' : color}18`
          }}>
            <Icon sx={{ fontSize: 22, color: urgent ? '#c62828' : color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // ── Nav Tile ───────────────────────────────────────────────────────────────
  const Tile = ({ icon: Icon, label, desc, color, path }) => (
    <Card elevation={1} sx={{
      borderRadius: 2, cursor: 'pointer', height: '100%',
      transition: 'transform 0.15s, box-shadow 0.15s',
      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
    }} onClick={() => navigate(path)}>
      <CardContent sx={{ py: 1.5, px: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{
            width: 36, height: 36, flexShrink: 0, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${color}18`
          }}>
            <Icon sx={{ fontSize: 20, color }} />
          </Box>
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={700} lineHeight={1.2} noWrap>{label}</Typography>
            {desc && <Typography variant="caption" color="text.secondary" lineHeight={1.2}>{desc}</Typography>}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // ── Section ────────────────────────────────────────────────────────────────
  const Section = ({ title, color, tiles }) => (
    <Box mb={3.5}>
      <Box display="flex" alignItems="center" mb={1.5}>
        <Box sx={{ width: 4, height: 20, bgcolor: color, borderRadius: 2, mr: 1.5, flexShrink: 0 }} />
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">{title}</Typography>
        <Divider sx={{ flex: 1, ml: 1.5 }} />
      </Box>
      <Grid container spacing={1.5}>
        {tiles.map(t => (
          <Grid item xs={12} sm={6} md={3} key={t.label}>
            <Tile {...t} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2
        }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Finance Manager</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
              General Ledger · Receivables · Payables · Banks · Fixed Assets
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh KPIs">
              <IconButton onClick={fetchKpis} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Admin Dashboard">
              <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── KPI Activities ── */}
        <Box mb={1}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Activities</Typography>
            <Divider sx={{ flex: 1, ml: 1.5 }} />
            {loading && <CircularProgress size={16} sx={{ ml: 1.5 }} />}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Cue icon={PendingActions} label="Open Student Invoices" color="#1565c0"
                value={loading ? '…' : fmtN(kpis?.openInvCount)}
                sub={loading ? '' : `${sym()} ${fmt(kpis?.openInvAmt)} outstanding`}
                path="/invoices" />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Cue icon={Warning} label="Overdue Invoices" color="#e65100" urgent={kpis?.overdueCount > 0}
                value={loading ? '…' : fmtN(kpis?.overdueCount)}
                sub="Past due date, unpaid"
                path="/invoices" />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Cue icon={CheckCircleOutline} label="Receipts This Month" color="#2e7d32"
                value={loading ? '…' : fmtN(kpis?.monthRecCount)}
                sub={loading ? '' : `${sym()} ${fmt(kpis?.monthAmt)} received`}
                path="/receipts" />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Cue icon={Description} label="Open Purchase Invoices" color="#6a1b9a"
                value={loading ? '…' : fmtN(kpis?.openPinvCount)}
                sub="Awaiting payment"
                path="/payables/invoices" />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Cue icon={TrendingUp} label="Pending Vouchers" color="#00695c"
                value={loading ? '…' : fmtN(kpis?.pendingPvCount)}
                sub="Draft / open vouchers"
                path="/payables/vouchers" />
            </Grid>
          </Grid>
        </Box>

        {/* ── Money In vs Money Out (cash basis, year to date) ── */}
        <Box mt={3}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Money In vs Money Out</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Cash basis · {new Date().getFullYear()} year to date
            </Typography>
            <Divider sx={{ flex: 1, ml: 1.5 }} />
          </Box>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="stretch">
                {/* Money In */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#2e7d3212', height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <ArrowDownward sx={{ color: '#2e7d32', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={600} color="text.primary">Money In</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#2e7d32">
                      {loading ? '…' : `${sym()} ${fmt(cash?.cashIn || 0)}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {loading ? '' : `${fmtN(cash?.receiptCount || 0)} receipts collected`}
                    </Typography>
                  </Box>
                </Grid>
                {/* Money Out */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#c6282812', height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <ArrowUpward sx={{ color: '#c62828', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={600} color="text.primary">Money Out</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#c62828">
                      {loading ? '…' : `${sym()} ${fmt(cash?.cashOut || 0)}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {loading ? '' : `${fmtN(cash?.voucherCount || 0)} vendor payments`}
                    </Typography>
                  </Box>
                </Grid>
                {/* Net */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, borderRadius: 2, height: '100%',
                    bgcolor: (cash?.net || 0) >= 0 ? '#1565c012' : '#e6510012' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <TrendingUp sx={{ color: (cash?.net || 0) >= 0 ? '#1565c0' : '#e65100', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={600} color="text.primary">Net Cash Flow</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={800}
                      color={(cash?.net || 0) >= 0 ? '#1565c0' : '#e65100'}>
                      {loading ? '…' : `${sym()} ${fmt(cash?.net || 0)}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(cash?.net || 0) >= 0 ? 'Surplus — more in than out' : 'Deficit — more out than in'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Monthly in/out bars */}
              {cash?.series?.length > 0 && (() => {
                const peak = Math.max(1, ...cash.series.map(m => Math.max(m.in, m.out)));
                const MLABEL = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                return (
                  <Box mt={3}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>By month</Typography>
                    <Box display="flex" alignItems="flex-end" gap={1.5} mt={1} sx={{ overflowX: 'auto', pb: 1 }}>
                      {cash.series.map(m => {
                        const mo = MLABEL[parseInt(m.month.split('-')[1], 10) - 1] || m.month;
                        return (
                          <Box key={m.month} display="flex" flexDirection="column" alignItems="center" sx={{ minWidth: 40 }}>
                            <Box display="flex" alignItems="flex-end" gap={0.5} height={90}>
                              <Tooltip title={`In: ${sym()} ${fmt(m.in)}`}>
                                <Box sx={{ width: 12, borderRadius: '3px 3px 0 0', bgcolor: '#2e7d32',
                                  height: `${Math.round((m.in / peak) * 90)}px`, minHeight: m.in > 0 ? 2 : 0 }} />
                              </Tooltip>
                              <Tooltip title={`Out: ${sym()} ${fmt(m.out)}`}>
                                <Box sx={{ width: 12, borderRadius: '3px 3px 0 0', bgcolor: '#c62828',
                                  height: `${Math.round((m.out / peak) * 90)}px`, minHeight: m.out > 0 ? 2 : 0 }} />
                              </Tooltip>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.65rem' }}>{mo}</Typography>
                          </Box>
                        );
                      })}
                      <Box display="flex" flexDirection="column" gap={0.5} sx={{ ml: 1, alignSelf: 'center' }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#2e7d32' }} />
                          <Typography variant="caption" color="text.secondary">In</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#c62828' }} />
                          <Typography variant="caption" color="text.secondary">Out</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </CardContent>
          </Card>
        </Box>

        <Box my={3}><Divider /></Box>

        {/* ── General Ledger ── */}
        <Section title="General Ledger" color="#37474f" tiles={[
          { icon: AccountTree, label: 'Chart of Accounts', desc: 'All GL accounts',               color: '#37474f', path: '/gl-accounts' },
          { icon: ReceiptLong, label: 'General Journal',   desc: 'Manual balanced postings',      color: '#1565c0', path: '/finance/general-journal' },
          { icon: EditNote,    label: 'GL Entries',        desc: 'Posted general ledger entries', color: '#37474f', path: '/gl-accounts' },
          { icon: ManageSearch, label: 'Find Entries',      desc: 'Trace all entries for a document', color: '#00838f', path: '/finance/find-entries' },
          { icon: Balance,     label: 'Trial Balance',     desc: 'All accounts, Dr = Cr',         color: '#1565c0', path: '/finance/trial-balance' },
          { icon: Assessment,  label: 'Income Statement',  desc: 'Revenue − expenses = profit',   color: '#2e7d32', path: '/finance/income-statement' },
          { icon: AccountBalance, label: 'Balance Sheet',  desc: 'Assets = liabilities + equity', color: '#6a1b9a', path: '/finance/balance-sheet' },
          { icon: PieChart,    label: 'GL by Dimension',   desc: 'Slice the GL by dimension',     color: '#6a1b9a', path: '/finance/gl-by-dimension' },
          { icon: TrendingUp,  label: 'P&L by Dimension',  desc: 'Income/expense by department',  color: '#2e7d32', path: '/finance/pnl-by-dimension' },
          { icon: AccountBalanceWallet, label: 'Cash Flow', desc: 'Cash in/out by bank',          color: '#00838f', path: '/finance/cash-flow' },
          { icon: ReceiptLong, label: 'VAT Return',        desc: 'Output − input VAT (KRA)',      color: '#e65100', path: '/finance/vat-return' },
          { icon: ReceiptLong, label: 'Withholding Tax',   desc: 'WHT withheld from vendors',     color: '#e65100', path: '/finance/wht' },
          { icon: AccountBalance, label: 'Budget Entry',   desc: 'Set budgets per account/term',  color: '#1565c0', path: '/finance/budgets' },
          { icon: TrendingUp,  label: 'Budget vs Actual',  desc: 'Variance by account',           color: '#2e7d32', path: '/finance/budget-vs-actual' },
        ]} />

        {/* ── Receivables ── */}
        <Section title="Receivables — Students" color="#1565c0" tiles={[
          { icon: People,         label: 'Learners / Customers', desc: 'Student records & bio-data',      color: '#1565c0', path: '/learners'               },
          { icon: Receipt,        label: 'Student Invoices',     desc: 'Term fee invoices',               color: '#f57c00', path: '/invoices'               },
          { icon: Payment,        label: 'Payments Received',    desc: 'Individual payment receipts',     color: '#2e7d32', path: '/receipts'               },
          { icon: ReceiptLong,    label: 'Batch Receipts',       desc: 'Multi-learner receipt posting',   color: '#00695c', path: '/finance/batch-receipts' },
          { icon: Description,    label: 'Statements',           desc: 'Learner account statements',      color: '#0288d1', path: '/statements'             },
          { icon: Balance,        label: 'Balances / Debtors',   desc: 'Who owes what — by class/stage',  color: '#c62828', path: '/finance/balances'       },
          { icon: HourglassBottom, label: 'Aged Receivables',    desc: 'Debtors by 30/60/90 days',        color: '#c62828', path: '/finance/aged-receivables' },
          { icon: EditNote,       label: 'Credit Notes',         desc: 'Waivers & billing corrections',   color: '#6a1b9a', path: '/finance/credit-notes'   },
          { icon: Payments,       label: 'Daily Collections',    desc: 'Receipts by day & bank',          color: '#2e7d32', path: '/finance/collections'    },
          { icon: MonetizationOn, label: 'Fee Structures',       desc: 'Term fee templates by stage',     color: '#00838f', path: '/feestructures'         },
          { icon: Category,       label: 'Charge Items',         desc: 'Fee types & GL mappings',         color: '#6a1b9a', path: '/academics/charge-items' },
        ]} />

        {/* ── Finance Setups ── */}
        <Section title="Finance Setups" color="#455a64" tiles={[
          { icon: Settings,    label: 'Posting Setup',      desc: 'AR & advance GL mapping',         color: '#6a1b9a', path: '/setups/posting'        },
          { icon: Lock,        label: 'Posting Period Lock', desc: 'Allow-posting date window',      color: '#6a1b9a', path: '/setups/posting-period'  },
          { icon: Lock,        label: 'Year-End Close',     desc: 'Close & lock a fiscal year',     color: '#c62828', path: '/finance/year-end-close' },
          { icon: AccountTree, label: 'Dimensions',         desc: 'Department, Project, Fund…',      color: '#6a1b9a', path: '/setups/dimensions'     },
          { icon: Numbers,     label: 'Number Series',      desc: 'Document numbering',              color: '#455a64', path: '/setups/number-series'  },
          { icon: Tune,        label: 'VAT Posting Groups', desc: 'Tax rates & GL accounts',         color: '#e65100', path: '/setups/vat-posting-groups' },
          { icon: AccountTree, label: 'Chart of Accounts',  desc: 'GL account structure',            color: '#37474f', path: '/gl-accounts'           },
        ]} />

        {/* ── Payables ── */}
        <Section title="Payables — Vendors" color="#e65100" tiles={[
          { icon: Business,     label: 'Vendors / Suppliers', desc: 'Vendor master records',           color: '#e65100', path: '/suppliers'             },
          { icon: RequestQuote, label: 'Purchase Quotes',     desc: 'RFQ / vendor quotations',         color: '#00838f', path: '/payables/quotes'      },
          { icon: Description,  label: 'Purchase Invoices',   desc: 'Vendor invoices',                 color: '#6a1b9a', path: '/payables/invoices'    },
          { icon: Payment,      label: 'Payment Vouchers',    desc: 'Payments to vendors',             color: '#00695c', path: '/payables/vouchers'    },
          { icon: ShoppingCart, label: 'Purchase Orders',     desc: 'LPOs and purchase orders',        color: '#2e7d32', path: '/payables/orders'      },
          { icon: EditNote,     label: 'Vendor Credit Notes', desc: 'Returns & billing corrections',   color: '#6a1b9a', path: '/payables/credit-notes' },
          { icon: HourglassBottom, label: 'Aged Payables',    desc: 'What we owe vendors by age',      color: '#e65100', path: '/payables/aging'       },
        ]} />

        {/* ── Banks & Cash ── */}
        <Section title="Banks & Cash" color="#00838f" tiles={[
          { icon: AccountBalance,       label: 'Bank Accounts',       desc: 'Bank & cash accounts',        color: '#00838f', path: '/banks'  },
          { icon: AccountBalanceWallet, label: 'Bank Reconciliation', desc: 'Match ledger to statement',   color: '#00838f', path: '/finance/bank-reconciliation' },
        ]} />

        {/* ── Fixed Assets ── */}
        <Section title="Fixed Assets" color="#6a1b9a" tiles={[
          { icon: HomeWork,     label: 'Fixed Assets',       desc: 'Asset register',                   color: '#6a1b9a', path: '/fixed-assets'                 },
          { icon: AccountTree,  label: 'FA Classes',         desc: 'Asset classification',             color: '#8e24aa', path: '/fixed-assets/classes'         },
          { icon: Description,  label: 'FA Posting Groups',  desc: 'GL mapping per asset type',        color: '#7b1fa2', path: '/fixed-assets/posting-groups'  },
          { icon: EditNote,     label: 'FA Ledger Entries',  desc: 'Depreciation & disposal entries',  color: '#6a1b9a', path: '/fixed-assets'                 },
        ]} />

        {/* ── Stores & Inventory ── */}
        <Section title="Stores & Inventory" color="#2e7d32" tiles={[
          { icon: Inventory2,  label: 'Items & Services',   desc: 'Item master list',                  color: '#1565c0', path: '/payables/items'               },
          { icon: LocationOn,  label: 'Locations',          desc: 'Stores & warehouses',               color: '#00695c', path: '/setups/locations'             },
          { icon: ListAlt,     label: 'Store Requisitions', desc: 'Internal stock requests',           color: '#4527a0', path: '/payables/requisitions'        },
          { icon: SwapHoriz,   label: 'Transfer Orders',    desc: 'Stock transfers between stores',    color: '#00796b', path: '/procurement/transfers'        },
          { icon: EditNote,    label: 'Item Journals',       desc: 'Stock adjustments & postings',     color: '#4e342e', path: '/payables/item-journals'       },
        ]} />

      </Box>
    </Box>
  );
};

export default FinanceDashboard;
